import { DateOnly } from '@if/core/date'
import { EPaymentFrequency, EPaymentMethod } from '@if/core/types'
import { CalendarSimulationDto, EOperationType, LoanSummaryInformationResultDto } from '@src/api/loan/loan-dto'
import {
  transformCalendarActionFromDto,
  transformCalendarActionToDto,
  transformFullLoanFromDto,
  transformFullLoanToDto,
} from '@src/api/loan/loan-transformer'
import { AddFeeSchemaType } from '@src/components/LoanCalendar/EditFees/AddFee-schema'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EBankFailureCode, EFeeType, ELoanStatus, EOperationStatus, getFeeTypeFromProduct } from '../constants'
import { FullLoan, PaymentConfig } from './loan-calendar'
import { CustomerPaymentOperation, InvoiceOperation, OperationInformation } from './loan-operation'

export enum ECalendarSimulationAction {
  UpdateFrequency = 'NewFrequencyActionDto',
  MoveCalendar = 'MoveCalendarActionDto',
  Reset = 'ResetCalendarActionDto',
  EditPayments = 'EditPaymentsCalendarActionDto',
  ChangePaymentStatus = 'ChangePaymentStatusActionDto',
  AddFee = 'AddFeeActionDto',
  ChangeRegularPmt = 'ChangeRegularPaymentActionDto',
}

interface BaseCalendarSimulationAction<TActionType extends ECalendarSimulationAction> {
  actionType: TActionType
}

// Action for updating the loan calendar simulation frequency

export interface UpdatePaymentFrequencyCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.UpdateFrequency> {
  frequency: EPaymentFrequency
  firstPaymentDate: DateOnly
}

// Action for moving the loan calendar simulation

export interface MoveCalendarCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.MoveCalendar> {
  fromDate: DateOnly
  toDate: DateOnly
  reason: string
  paymentToRaiseIds: string[]
}

// Action for editing payments in the loan calendar simulation

export interface EditPayment {
  operationId: string
  effectiveDate?: DateOnly // null values indicate no change
  amount?: number
  paymentMethod?: EPaymentMethod
  note?: string | null
  sendSmsReminder?: boolean
}

export interface AddPayment {
  effectiveDate: DateOnly
  amount: number
  paymentMethod: EPaymentMethod
  note?: string | null
  sendSmsReminder: boolean
}

export interface EditPaymentsCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.EditPayments> {
  editedPayments: EditPayment[]
  newPayments: AddPayment[]
  deletedPaymentIds: string[]
}

export interface ChangePaymentStatusAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.ChangePaymentStatus> {
  newStatus: EOperationStatus
  paymentIds: string[]
  nsfFee: number | undefined
  transactionDate: DateOnly | undefined
  nsfStatus: EBankFailureCode | undefined
}

export interface AddFeeAction extends BaseCalendarSimulationAction<ECalendarSimulationAction.AddFee> {
  effectiveDate: DateOnly
  amount: number
  feeType: EFeeType
  note?: string | null
}

export interface ChangeRegularPmtAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.ChangeRegularPmt> {
  amount: number
  fromDate: DateOnly
}

// Add other simulation actions as needed with an union
export type CalendarSimulationAction =
  | UpdatePaymentFrequencyCalendarAction
  | MoveCalendarCalendarAction
  | EditPaymentsCalendarAction
  | ChangePaymentStatusAction
  | AddFeeAction
  | ChangeRegularPmtAction
  | BaseCalendarSimulationAction<ECalendarSimulationAction.Reset>

export interface SignedCalendarSimulation {
  signature: string
  simulation: CalendarSimulation
}

export interface ComputeCalendarSimulationAction extends Partial<SignedCalendarSimulation> {
  action: CalendarSimulationAction
}

export interface SpreadOverdueAmount {
  simulation: CalendarSimulation
  paymentsDate: DateOnly[]
}

export class CalendarSimulation {
  public readonly fullLoan: FullLoan
  public readonly actions: CalendarSimulationAction[]
  public readonly operationInformations: OperationInformation[]
  public readonly customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[]
  public readonly simulationExpiresOn: Date
  public readonly recalculateTransactionFrom: DateOnly | null
  public readonly summaryInformation: LoanSummaryInformationResultDto

  private _cachedLastExecutedOperation: OperationInformation | null | undefined = undefined

  private constructor(
    fullLoan: FullLoan,
    actions: CalendarSimulationAction[],
    operationInformations: OperationInformation[],
    customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[],
    simulationExpiresOn: Date,
    recalculateTransactionFrom: DateOnly | null,
    summaryInformation: LoanSummaryInformationResultDto,
  ) {
    this.fullLoan = fullLoan
    this.actions = actions
    this.operationInformations = operationInformations
    this.customerPaymentOperations = customerPaymentOperations
    this.simulationExpiresOn = simulationExpiresOn
    this.recalculateTransactionFrom = recalculateTransactionFrom
    this.summaryInformation = summaryInformation
  }

  public getRepaymentConfigAtDate(date: DateOnly): PaymentConfig {
    return this.fullLoan.paymentConfigs.findLast((pc) => pc.effectiveDate <= date)!
  }

  public getNextPaymentAfterDate(date: DateOnly): CustomerPaymentOperation | undefined {
    const allPaymentAfterDate = this.customerPaymentOperations.filter(
      (x) => x.isCustomerPaymentOperation() && x.operation.effectiveDate > date,
    )

    if (allPaymentAfterDate.length === 0) return undefined
    return allPaymentAfterDate[0].operation
  }

  public getAllLoanRepaymentConfigDates(): DateOnly[] {
    return this.fullLoan.paymentConfigs.map((pc) => pc.effectiveDate)
  }

  public getCurrentStatus(): ELoanStatus {
    return this.fullLoan.statusHistory.reduce((currentStatus, status) => {
      const effectiveDate = status.createdOn
      return effectiveDate > currentStatus.createdOn ? status : currentStatus
    }, this.fullLoan.statusHistory[0]).status
  }

  public getLastExecutedOperation(): OperationInformation | null {
    if (this._cachedLastExecutedOperation == undefined) {
      this._cachedLastExecutedOperation = this.operationInformations.findLast((oi) => oi.isExecuted()) || null
    }

    return this._cachedLastExecutedOperation
  }

  public getLastPaymentDate(): DateOnly {
    return this.customerPaymentOperations.length > 0
      ? this.customerPaymentOperations[this.customerPaymentOperations.length - 1].operation.effectiveDate
      : this.fullLoan.loan.lastPaymentOn
  }

  public getThreeNextPlannedPayments(): CustomerPaymentOperation[] {
    return this.customerPaymentOperations
      .filter(
        (operationInfo) =>
          operationInfo.operation.status === EOperationStatus.Planned &&
          operationInfo.operation.effectiveDate >= new DateOnly(),
      )
      .slice(0, 3)
      .map((operationInfo) => operationInfo.operation)
  }

  public getNearestOperationInformation() {
    const today = new DateOnly()
    return this.operationInformations.findLast(
      (oi) => oi.operation.effectiveDate && oi.operation.effectiveDate <= today,
    )
  }

  public hasExecutedPaymentsAfterDate(date: Date): boolean {
    return this.operationInformations.some(
      (oi) => oi.isCustomerPaymentOperation() && oi.isExecuted() && oi.operation.effectiveDate >= date,
    )
  }

  public getTotalFundedProductsAmount(): number {
    return this.fullLoan.loan.fundedProducts.reduce((total: number, product) => total + product.amount, 0)
  }

  /**
   * Get the next valid action date based on the payment method and executed operations
   * Any batch action done on the simulation must be after this date
   * Since you cannot do actions in the past (or before the minimum delay for the payment method) or on executed payments
   * @param paymentMethod The payment method to get the next valid action date for
   * @returns The next valid action date
   */
  public getNextValidActionDate(
    paymentMethod: EPaymentMethod = EPaymentMethod.PreAuthorizedPayment,
    canBypassAcpLock = false,
  ): DateOnly {
    const minimumDate = SafePaymentActionDateService.getInstance().getFirstSafeDate(paymentMethod, canBypassAcpLock)
    const lastExecutedOperationDate = this.getLastExecutedOperation()?.operation.effectiveDate
    if (lastExecutedOperationDate && lastExecutedOperationDate > minimumDate) return lastExecutedOperationDate
    return minimumDate
  }

  /**
   * Check if the simulation has expired based on its TTL
   * A simulation is expired when the current time exceeds simulationExpiresOn
   *
   * @param now - Optional current time for testing (defaults to Date.now())
   * @returns true if the simulation has expired, false if it's still valid
   */
  public isExpired(now: Date = new Date()): boolean {
    return this.simulationExpiresOn.getTime() <= now.getTime()
  }

  public getExistingFees(): AddFeeSchemaType[] {
    return this.operationInformations
      .filter((opInfo) => {
        if (opInfo.operation._internalType !== EOperationType.InvoiceOperation) return false
        return opInfo.operation.invoiceLines.some((line) => getFeeTypeFromProduct(line.productId) !== null)
      })
      .flatMap((opInfo) => {
        const invoiceOp = opInfo.operation as InvoiceOperation
        const feeLines = invoiceOp.invoiceLines.filter((line) => getFeeTypeFromProduct(line.productId) !== null)

        const groupedByType = Map.groupBy(feeLines, (line) => getFeeTypeFromProduct(line.productId)!)

        return [...groupedByType.entries()].map(([feeType, lines]) => ({
          feeType,
          amount: lines.reduce((sum, line) => sum + line.amount, 0),
          effectiveDate: invoiceOp.effectiveDate,
          note: invoiceOp.note ?? '',
        }))
      })
  }

  public static fromDto(calendarSimulationDto: CalendarSimulationDto): CalendarSimulation {
    const {
      fullLoan,
      actions,
      operationInformations,
      simulationExpiresOn,
      recalculateTransactionFrom,
      loanSummaryInformation: summaryInformation,
    } = calendarSimulationDto
    const customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[] = []
    const operationInfos: OperationInformation[] = []
    let paymentNumberIndex = 1

    for (const info of operationInformations) {
      let oi: OperationInformation
      if (info.operation._internalType === EOperationType.CustomerPaymentOperation) {
        oi = OperationInformation.fromDto(info, paymentNumberIndex++)
        customerPaymentOperations.push(oi as OperationInformation<CustomerPaymentOperation>)
      } else {
        oi = OperationInformation.fromDto(info)
      }
      operationInfos.push(oi)
    }

    return new CalendarSimulation(
      transformFullLoanFromDto(fullLoan),
      actions.map((actionDto) => transformCalendarActionFromDto(actionDto)),
      operationInfos,
      customerPaymentOperations,
      new Date(simulationExpiresOn),
      recalculateTransactionFrom ? new DateOnly(recalculateTransactionFrom) : null,
      summaryInformation,
    )
  }

  public static toDto(calendarSimulation: CalendarSimulation): CalendarSimulationDto {
    return {
      fullLoan: transformFullLoanToDto(calendarSimulation.fullLoan),
      actions: calendarSimulation.actions.map((action) => transformCalendarActionToDto(action)),
      operationInformations: calendarSimulation.operationInformations.map((info) => OperationInformation.toDto(info)),
      simulationExpiresOn: calendarSimulation.simulationExpiresOn.toISOString(),
      recalculateTransactionFrom: calendarSimulation.recalculateTransactionFrom
        ? new DateOnly(calendarSimulation.recalculateTransactionFrom).toString()
        : null,
      loanSummaryInformation: calendarSimulation.summaryInformation,
    }
  }
}
